/**
 * AiOutline.js — Typed outline parser, serializer and renderer for Atlas
 *
 * The "outline" is a UTF-8 text format, one node per line, with a semantic
 * prefix at the start of each line. It travels between /describe and
 * /generate-bpmn as the value of `structured_description`. See
 * `plans/actualizacion/ATLAS_BPMN_GENERATION_PLAN.md` §2.1 for the spec.
 *
 * Public API:
 *   - PREFIX_META          metadata per known prefix (symbol, css class, label)
 *   - parseOutline(text)   → array of { prefix, label, actor, branch, indent, raw, lineNumber, unknown }
 *   - serializeOutline(ns) → string (round-trip with parseOutline normalizing indent to 2)
 *   - validateOutline(ns)  → array of { line, code, message, severity }
 *   - renderOutlineHtml(text, container)  paints the outline coloured into a DOM node
 */

export const PREFIX_META = {
  INICIO:     { sym: '▶', cls: 'start',      label: 'INICIO' },
  FIN:        { sym: '⏹', cls: 'end',        label: 'FIN' },
  TAREA:      { sym: '▣', cls: 'task',       label: 'TAREA' },
  DECISION:   { sym: '◆', cls: 'decision',   label: 'DECISION' },
  GATEWAY:    { sym: '▤', cls: 'gateway',    label: 'GATEWAY' },
  EVENTO:     { sym: '◉', cls: 'event',      label: 'EVENTO' },
  SUBPROCESO: { sym: '▦', cls: 'subprocess', label: 'SUBPROCESO' },
  MENSAJE:    { sym: '✉', cls: 'message',    label: 'MENSAJE' },
};

const KNOWN_PREFIXES = Object.keys(PREFIX_META);
const BRANCH_PARENT_PREFIXES = new Set(['DECISION', 'GATEWAY']);

// Match line:
//  - optional indent (group 1)
//  - optional branch arrow "→ <label>:" (group 2)
//  - prefix (group 3)
//  - optional " (actor)" (group 4)
//  - ":" + label (group 5)
// Examples:
//   "TAREA (Empleado): Completar formulario"
//   "  → SÍ: TAREA (RRHH): Registrar en sistema"
//   "  → NO: FIN: Solicitud rechazada"
const LINE_RE = /^(\s*)(?:→\s+([^:]+?):\s*)?([A-ZÁÉÍÓÚÑ_]+)(?:\s*\(([^)]*)\))?\s*:\s*(.*)$/u;

/**
 * Parse a typed outline (UTF-8 text, one node per line) into an array of nodes.
 *
 * Tolerates:
 *  - blank/whitespace-only lines (skipped)
 *  - 2-space or 4-space indentation (normalised: each 2 chars => 1 indent unit)
 *  - unknown prefixes (returned with `unknown: true` so caller can warn)
 *
 * @param {string} text
 * @returns {Array<{prefix: string, label: string, actor: ?string, branch: ?string, indent: number, raw: string, lineNumber: number, unknown: boolean}>}
 */
export function parseOutline(text) {
  if (typeof text !== 'string' || !text.trim()) return [];
  const out = [];
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  lines.forEach((raw, idx) => {
    const lineNumber = idx + 1;
    if (!raw || !raw.trim()) return;

    const m = raw.match(LINE_RE);
    if (!m) {
      // Unparseable line → coerce to TAREA with the whole trimmed line as label.
      out.push({
        prefix: 'TAREA',
        label: raw.trim(),
        actor: null,
        branch: null,
        indent: 0,
        raw,
        lineNumber,
        unknown: true,
      });
      return;
    }

    const [, leadingWs, branch, prefixRaw, actor, label] = m;
    // Convert leading whitespace to indent units (2 spaces = 1). Tabs count as 2 spaces.
    const expandedWs = leadingWs.replace(/\t/g, '  ');
    const indent = Math.floor(expandedWs.length / 2);

    const prefix = (prefixRaw || '').toUpperCase();
    const isKnown = KNOWN_PREFIXES.includes(prefix);

    out.push({
      prefix: isKnown ? prefix : 'TAREA',
      originalPrefix: isKnown ? prefix : prefix,
      label: (label || '').trim(),
      actor: actor ? actor.trim() : null,
      branch: branch ? branch.trim() : null,
      indent,
      raw,
      lineNumber,
      unknown: !isKnown,
    });
  });

  return out;
}

/**
 * Serialize an array of nodes back to canonical outline text.
 * Indentation is normalized to 2 spaces per indent unit.
 *
 * @param {Array} nodes  output of parseOutline
 * @returns {string}
 */
export function serializeOutline(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return '';
  return nodes.map((n) => {
    const indent = '  '.repeat(Math.max(0, n.indent || 0));
    const branch = n.branch ? `→ ${n.branch}: ` : '';
    const prefix = n.unknown && n.originalPrefix ? n.originalPrefix : n.prefix;
    const actor = n.actor ? ` (${n.actor})` : '';
    return `${indent}${branch}${prefix}${actor}: ${n.label}`;
  }).join('\n');
}

/**
 * Validate parsed nodes against the rules in §4.5 of the plan.
 *
 * Returns issues with `severity` 'error' | 'warning'. The caller decides what
 * to surface in the UI; `error` typically disables the "Generate BPMN" button.
 *
 * @param {Array} nodes
 * @returns {Array<{line: number, code: string, message: string, severity: 'error'|'warning'}>}
 */
export function validateOutline(nodes) {
  const issues = [];
  if (!Array.isArray(nodes) || nodes.length === 0) {
    issues.push({ line: 0, code: 'empty', message: 'El outline está vacío.', severity: 'error' });
    return issues;
  }

  let hasInicio = false;
  let hasFin = false;

  nodes.forEach((n, i) => {
    if (n.prefix === 'INICIO') hasInicio = true;
    if (n.prefix === 'FIN') hasFin = true;

    if (n.unknown) {
      issues.push({
        line: n.lineNumber,
        code: 'unknown_prefix',
        message: `Prefijo desconocido "${n.originalPrefix || ''}" — interpretado como TAREA.`,
        severity: 'warning',
      });
    }

    if (!n.label || !n.label.trim()) {
      issues.push({
        line: n.lineNumber,
        code: 'missing_label',
        message: 'Línea sin etiqueta (solo prefijo).',
        severity: 'error',
      });
    }

    if (n.branch) {
      // Branch line. Look for a parent (DECISION/GATEWAY) earlier with smaller indent.
      let parentFound = false;
      for (let j = i - 1; j >= 0; j--) {
        const prev = nodes[j];
        if (prev.indent < n.indent && BRANCH_PARENT_PREFIXES.has(prev.prefix)) {
          parentFound = true; break;
        }
        if (prev.indent < n.indent) break; // hit a non-branch parent
      }
      if (!parentFound) {
        issues.push({
          line: n.lineNumber,
          code: 'orphan_branch',
          message: 'Rama sin DECISION/GATEWAY padre.',
          severity: 'warning',
        });
      }
    } else if (n.indent > 0) {
      issues.push({
        line: n.lineNumber,
        code: 'indented_without_branch',
        message: 'Línea indentada sin etiqueta de rama "→ <branch>:".',
        severity: 'warning',
      });
    }
  });

  if (!hasInicio) {
    issues.push({ line: 0, code: 'missing_start', message: 'El outline no tiene nodo INICIO.', severity: 'error' });
  }
  if (!hasFin) {
    issues.push({ line: 0, code: 'missing_end', message: 'El outline no tiene nodo FIN.', severity: 'error' });
  }
  if (nodes.length > 50) {
    issues.push({ line: 0, code: 'too_many_nodes', message: `Outline muy largo (${nodes.length} nodos, máx recomendado 50).`, severity: 'warning' });
  }

  return issues;
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Render the outline as coloured rows inside `container`.
 * Replaces existing children. Uses CSS classes from §3.2.
 *
 * @param {string} text
 * @param {HTMLElement} container
 * @param {Object} [opts]
 * @param {Map<number, Array>|Object<number, Array>} [opts.issuesByLine]  per-line issues, keyed by 1-based lineNumber
 */
export function renderOutlineHtml(text, container, opts = {}) {
  if (!container) return;
  const nodes = parseOutline(text || '');
  container.innerHTML = '';

  // Normalise issuesByLine into a Map for O(1) lookup. Accepts either a Map
  // or a plain object keyed by line number (the AiAssistant builds it from
  // the lint response).
  const issuesByLine = opts.issuesByLine instanceof Map
    ? opts.issuesByLine
    : (opts.issuesByLine && typeof opts.issuesByLine === 'object'
        ? new Map(Object.entries(opts.issuesByLine).map(([k, v]) => [Number(k), v]))
        : new Map());

  if (!nodes.length) {
    const empty = document.createElement('div');
    empty.className = 'atlas-out atlas-out--empty';
    empty.textContent = '(sin contenido)';
    container.appendChild(empty);
    return;
  }

  // Width of the line-number gutter, scaled to the largest line number so
  // single-digit and triple-digit outlines line up cleanly.
  const maxLine = nodes.reduce((m, n) => Math.max(m, n.lineNumber || 0), 0);
  const linenoWidth = String(maxLine).length;

  for (const n of nodes) {
    const meta = PREFIX_META[n.prefix] || PREFIX_META.TAREA;
    const row = document.createElement('div');
    const lineIssues = issuesByLine.get(n.lineNumber) || [];
    const hasError   = lineIssues.some(i => i.severity === 'error');
    const hasWarning = !hasError && lineIssues.some(i => i.severity === 'warning');

    const classes = [`atlas-out`, `atlas-out--${meta.cls}`];
    if (hasError)   classes.push('has-error');
    else if (hasWarning) classes.push('has-warning');
    row.className = classes.join(' ');
    row.dataset.line = String(n.lineNumber);
    if (n.indent > 0) row.classList.add('is-indented');

    const parts = [];
    parts.push(
      `<span class="atlas-out__lineno" aria-hidden="true" style="min-width:${linenoWidth + 1}ch">${n.lineNumber}</span>`
    );
    parts.push(
      `<span class="atlas-out__indent" aria-hidden="true" style="width:${(n.indent || 0) * 16}px"></span>`
    );
    if (n.indent > 0) {
      parts.push(`<span class="atlas-out__guide" aria-hidden="true"></span>`);
    }
    parts.push(`<span class="atlas-out__sym" aria-hidden="true">${meta.sym}</span>`);
    if (n.branch) {
      parts.push(`<span class="atlas-out__branch">→ ${escapeHtml(n.branch)}</span>`);
    }
    parts.push(`<span class="atlas-out__prefix tok-prefix-${escapeHtml(n.prefix)}">${escapeHtml(n.prefix)}</span>`);
    parts.push(`<span class="atlas-out__label">${escapeHtml(n.label)}</span>`);
    if (n.actor) {
      parts.push(`<span class="atlas-out__actor"><span class="atlas-out__actor-pill">${escapeHtml(n.actor)}</span></span>`);
    }
    if (n.unknown) {
      parts.push(`<span class="atlas-out__warn" title="Prefijo desconocido — interpretado como TAREA">⚠</span>`);
    }
    if (lineIssues.length > 0) {
      const tip = lineIssues.map(i => i.message).join('\n');
      const badge = hasError ? '⛔' : '⚠️';
      parts.push(`<span class="atlas-out__issue" title="${escapeHtml(tip)}">${badge}</span>`);
    }
    row.innerHTML = parts.join('');
    container.appendChild(row);
  }
}
